import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, CircleCheck, Sparkles } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import { useVisaoComissaoSimulada } from '@/hooks/comissao/useVisaoComissaoSimulada';
import { useComissaoBundleVisao } from '@/hooks/comissao/useComissaoBundleVisao';
import { descontoEsim } from '../../../../backend/src/domain/qualidade/produtividade-ativacao';
import { cn } from '@/lib/utils';

/**
 * ANÁLISE DO DIA + ATIVAÇÃO DO DIA (Douglas 25/09/2026).
 * O selo D1/D2/D3 ao lado do cliente (fechado; abre no clique para não poluir
 * a linha) e o card do aviso, montado no backend pelo cargo de quem abre.
 */

export type AnaliseCaso = {
  dia: number; veredito: 'falta_acao_nossa' | 'cliente_sem_retorno' | 'andando' | 'divergente'; status_real: string; motivo: string; proximo: string; data: string;
  historico: { data: string; dia: number; veredito: string; motivo: string }[];
};
export type LinhaCard = { rotulo: string; valor: string; destaque?: boolean; consultorId?: string };
export type CardAtivacaoDia = {
  papel: string; titulo?: string; explicacao?: string; competencia?: string; desconto?: { prazo: string } | null; consultorId?: string; subtitulo: string; rotuloMeta: string; valor: string; legenda: string; pct: number | null;
  marcas?: { pct: number; rotulo: string }[]; alerta: string | null; titulosLista: string; lista: LinhaCard[];
  botao: string; url: string; data: string | null;
};

async function pedir<T>(caminho: string): Promise<T> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Sua sessão expirou. Entre novamente.');
  const base = ((import.meta.env.VITE_SUPABASE_URL as string) || 'https://beta.crm-api.primetecia.com.br/api').replace(/\/$/, '');
  const r = await fetch(`${base}/v1/qualidade/${caminho}`, { headers: { Authorization: `Bearer ${session.access_token}` } });
  const corpo = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(corpo?.error || 'Não foi possível carregar a análise do dia.');
  return corpo.data as T;
}

export function useAnalisesDia() {
  return useQuery({ queryKey: ['qualidade', 'analise-dia'], staleTime: 5 * 60_000, refetchOnWindowFocus: false,
    queryFn: () => pedir<Record<string, AnaliseCaso>>('analise-dia') });
}

export function useAtivacaoDia(ligado = true) {
  return useQuery({ queryKey: ['qualidade', 'ativacao-dia'], enabled: ligado, staleTime: 5 * 60_000, refetchOnWindowFocus: false,
    queryFn: () => pedir<CardAtivacaoDia>('ativacao-dia') });
}

const ROTULO_VEREDITO: Record<AnaliseCaso['veredito'], string> = { falta_acao_nossa: 'Falta ação nossa', cliente_sem_retorno: 'Cliente não respondeu', andando: 'Andando', divergente: 'Status errado na tela' };

export function SeloAnaliseDia({ analise }: { analise?: AnaliseCaso }) {
  if (!analise) return null;
  const forte = analise.veredito === 'falta_acao_nossa' || analise.veredito === 'divergente';
  return <Popover>
    <PopoverTrigger asChild>
      <button type="button" aria-label={`Análise do dia: D${analise.dia}, ${ROTULO_VEREDITO[analise.veredito]}`}
        className={cn('inline-flex h-[16px] shrink-0 items-center gap-0.5 rounded border px-1 text-[9.5px] font-semibold leading-none',
          forte ? 'border-primary/60 text-primary' : 'border-border text-muted-foreground')}>
        <Sparkles className="h-2.5 w-2.5" aria-hidden />D{analise.dia}
      </button>
    </PopoverTrigger>
    <PopoverContent align="start" className="w-[340px] p-3 text-[11px]">
      <p className="font-semibold">Análise de {analise.data.slice(8, 10)}/{analise.data.slice(5, 7)} · D{analise.dia}</p>
      <p className="mt-1"><span className={cn('font-semibold', forte && 'text-primary')}>{ROTULO_VEREDITO[analise.veredito]}:</span> {analise.motivo}</p>
      <p className="mt-1 text-muted-foreground">Status real: {analise.status_real}</p>
      <p className="mt-1 text-muted-foreground">Próximo: {analise.proximo}</p>
      {!!analise.historico.length && <ol className="mt-2 space-y-0.5 border-t border-border pt-2 text-muted-foreground">
        {analise.historico.slice(-4).map(h => <li key={h.data}>D{h.dia} · {ROTULO_VEREDITO[h.veredito as AnaliseCaso['veredito']] || h.veredito} · {h.motivo}</li>)}
      </ol>}
      <p className="mt-2 text-[10px] text-muted-foreground">Leitura da IA às 7h. Não muda o status: quem move é você.</p>
    </PopoverContent>
  </Popover>;
}

const brl = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });

/** Comissão que o consultor perde se o mês fechasse hoje: linhas PN/HA ainda
 *  sem eSIM confirmado × o % de PN/HA da faixa em que ele está agora. A conta é
 *  a mesma do fechamento (calcularDescontoLinhasAtivacao); só adianta o prazo. */
function useComissaoEmRisco(consultorId: string | undefined, competencia: string | undefined) {
  const data = competencia ? new Date(`${competencia}-01T12:00:00`) : new Date();
  const ligado = !!consultorId && !!competencia;
  const { resultado } = useVisaoComissaoSimulada(ligado ? consultorId : undefined, 'consultor', data, { leve: true });
  const { data: bundle } = useComissaoBundleVisao(data);
  const base = bundle?.ativacao;
  if (!ligado || !base?.habilitada || !resultado) return null;
  const linhas = base.linhas.filter(l => l.consultorId === consultorId && l.pendente);
  if (!resultado.bateuMeta) return { valor: 0, linhas: linhas.length, bateuMeta: false };
  const valor = descontoEsim(base, { pessoaId: consultorId, idsEscopo: [consultorId!], cargo: 'CONSULTOR', bateuMeta: true,
    percentuais: { pn: resultado.percentuais.pn || 0, ha: resultado.percentuais.ha || 0 } });
  return { valor, linhas: linhas.length, bateuMeta: true };
}

function RiscoConsultor({ card }: { card: CardAtivacaoDia }) {
  const r = useComissaoEmRisco(card.consultorId, card.competencia);
  if (!card.desconto) return null;
  if (!r) return <p className="text-[12px] text-muted-foreground">Calculando a comissão em risco…</p>;
  return <p className="rounded-md border border-primary/40 px-3 py-2 font-semibold text-primary">
    {r.bateuMeta
      ? `${brl(r.valor)} de comissão em risco · ${r.linhas} linhas PN/HA sem eSIM confirmado. Confirme até ${card.desconto.prazo} para não perder.`
      : `Se bater a meta, as ${r.linhas} linhas PN/HA sem eSIM confirmado saem da comissão. Prazo: ${card.desconto.prazo}.`}
  </p>;
}

function RiscoLinha({ consultorId, competencia }: { consultorId?: string; competencia?: string }) {
  const r = useComissaoEmRisco(consultorId, competencia);
  if (!r) return null;
  return <span className="block text-[10.5px] font-normal text-muted-foreground">{r.bateuMeta ? `${brl(r.valor)} de comissão em risco` : 'meta ainda não batida'}</span>;
}

export function ConteudoAtivacaoDia({ card, onAbrir }: { card: CardAtivacaoDia; onAbrir?: () => void }) {
  const navigate = useNavigate();
  return <div className="space-y-3 text-[13px]">
    {card.explicacao && <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-[12px] leading-snug">{card.explicacao}</p>}
    <p className="text-[11px] text-muted-foreground">{card.subtitulo}{card.data ? ` · análise de ${card.data}` : ''}</p>
    <div>
      <p className="text-[11px] text-muted-foreground">{card.rotuloMeta}</p>
      <div className="flex items-baseline gap-2"><span className="font-display text-[26px] font-bold tabular-nums">{card.valor}</span>
        <span className="text-[12px] text-muted-foreground">{card.legenda}</span></div>
      {card.pct != null && <div className="relative mt-2 h-2 rounded bg-muted">
        <div className="h-2 rounded bg-primary" style={{ width: `${Math.min(100, card.pct)}%` }} />
        {card.marcas?.map(m => <span key={m.pct} className="absolute -top-1 h-4 w-px bg-muted-foreground" style={{ left: `${m.pct}%` }} title={m.rotulo} />)}
      </div>}
      {!!card.marcas?.length && <p className="mt-1 text-[10.5px] text-muted-foreground">{card.marcas.map(m => m.rotulo).join(' · ')}</p>}
      {card.alerta && <p className="mt-2 font-semibold text-primary">{card.alerta}</p>}
    </div>
    {card.papel === 'consultor' && <RiscoConsultor card={card} />}
    <div>
      <p className="mb-1 text-[11px] text-muted-foreground">{card.titulosLista}</p>
      {card.lista.length ? card.lista.map((l, i) => <div key={i} className="flex justify-between gap-3 border-t border-border py-1">
        <span className="truncate">{l.rotulo}</span><span className={cn('shrink-0 text-right font-semibold tabular-nums', l.destaque && 'text-primary')}>{l.valor}
          {card.desconto && card.papel === 'supervisor' && l.consultorId && <RiscoLinha consultorId={l.consultorId} competencia={card.competencia} />}</span>
      </div>) : <p className="border-t border-border py-1 text-muted-foreground">Nada parado hoje.</p>}
    </div>
    <button type="button" onClick={() => { onAbrir?.(); navigate(card.url); }}
      className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand-borgonha px-5 py-2.5 text-sm font-medium text-white hover:bg-brand-vinho">
      {card.botao} <ArrowRight className="h-4 w-4" />
    </button>
  </div>;
}

export function CorpoAvisoAtivacaoDia({ onAbrir }: { onAbrir?: () => void }) {
  const q = useAtivacaoDia();
  if (q.isLoading) return <p className="text-sm text-muted-foreground">Carregando os números de hoje…</p>;
  if (q.error || !q.data) return <p className="text-sm text-destructive">{(q.error as Error)?.message || 'Sem dados.'}</p>;
  return <ConteudoAtivacaoDia card={q.data} onAbrir={onAbrir} />;
}

export const TITULO_AVISO_DO_DIA = 'Validação do eSIM';

export function BotaoAtivacaoDia({ className }: { className?: string }) {
  const [aberto, setAberto] = useState(false);
  const q = useAtivacaoDia(aberto);
  return <>
    <button type="button" onClick={() => setAberto(true)} className={className}>
      <CircleCheck className="mr-1 inline h-3 w-3" aria-hidden />Resumo do dia
    </button>
    <Dialog open={aberto} onOpenChange={setAberto}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="text-brand-borgonha">{q.data?.titulo || TITULO_AVISO_DO_DIA}</DialogTitle></DialogHeader>
        {aberto && <CorpoAvisoAtivacaoDia onAbrir={() => setAberto(false)} />}
      </DialogContent>
    </Dialog>
  </>;
}
